import { Apollo, State2Way } from "@/decorators";
import gql from "graphql-tag";
import moment from "moment-timezone";
import { orderBy } from "lodash";

import { newCommonFormatCurrency } from "../../../backend/src/common/services/mathServices";
import { isAccountingZero } from "../services/mathServices";
import { User } from "../store/store";

import { getChatStatus } from "../services/chatServices";
import { getMostRecentAccountingPeriod } from "@/services/accountingPeriods";

import { isDateOfToday, formatDateForUI } from "@/helpers/date.helper";
import { getBTMinMaxQueryVariables, getCommonBTQueryVariables } from "../services/graphqlServices";

import { AccountableAccount, BankAccount } from "../../../types";
import { AuditCheckBankKey } from "@/types/auditCheck.types";

import AUDIT_BT_ITEMS from "../graphql/audit/AuditBTItems.gql";
import AUDIT_ITEMS from "@/graphql/audit/AuditItems.gql";
import AUTO_IMPORT_BANK_TRANSACTIONS from "../graphql/BankTransactionsAutoImport.gql";
import BANKTRANSACTIONS_FILTER_QUERY from "../graphql/bankTransaction/FilterBankTransactions.gql";
import RECONCILIATE_COMPANY from "../graphql/reconciliation/ReconciliateCompany.gql";
import VALIDATE_RECONCILIATION from "../graphql/reconciliation/ValidateReconciliation.gql";

import DELETE_BANK_TRANSACTIONS from "../graphql/bankTransaction/DeleteBankTransactions.gql";
import MIN_MAX_BANK_TRANSACTIONS from "../graphql/bankTransaction/MinMaxBankTransactions.gql";
import REMOVE_LINKED_ACCOUNTING_LINES from "../graphql/bankTransaction/RemoveLinkedAccountingLines.gql";

import {
    socialAccountableAccounts,
    stateAccountableAccounts,
} from "../services/accountableAccountServices";
import { connectBank } from "../services/bankServices";
import {
    defaultBankTransactionFilter,
    openBankTransaction,
    updateBankTransactionAccountableAccount,
} from "../services/bankTransactionServices";

import {
    isInsideClosedAccountingPeriod,
    isInsideAccountingClosing,
} from "../services/accountingPeriods";
import { getLinkedDocumentId } from "../services/bncServices";
import { DialogAction } from "../services/dialogAction";
import {
    createReconciliationsWithSalesAuto,
    linkBankTransactionWithCashBox,
} from "../services/reconciliationServices";
import { UserActivity } from "../services/userActivityServices";
import { getUserDisplayName } from "../services/userServices";

import { ComponentBase } from "./ComponentBase";

import { isNotSubjectToVAT } from "@/services/vat/vatHelpers.services";
import { Component, Watch, Ref } from "vue-facing-decorator";
import colors from "vuetify/util/colors";
import { checkAccessRights } from "../services/accessRightServices";

/**
 * Compute the last reconciled balances for all accounts.
 * If all accounts have the same last reconciled balance date,
 * it sets the last reconciled balance for the "ALL" account.
 * @param this - The current context of the component.
 */
function computeLastReconciledBalances(this: any) {
    let haveLastReconciledBalancesSameDate = true;
    let previousLastReconciledBalanceDate;
    let totalBalance = 0;

    for (const account of this.accounts) {
        const auditItem = this.auditItemsAccountBalance.find((item) => item.id === account.id);
        if (auditItem) {
            account.lastReconciledBalance = auditItem.lastReconciledBalance;

            if (previousLastReconciledBalanceDate) {
                if (previousLastReconciledBalanceDate !== auditItem.lastReconciledBalance?.date) {
                    haveLastReconciledBalancesSameDate = false;
                }
            }

            totalBalance = totalBalance + auditItem.lastReconciledBalance?.balance;
            previousLastReconciledBalanceDate = auditItem.lastReconciledBalance?.date;
        }
    }

    if (haveLastReconciledBalancesSameDate) {
        const account = this.accounts.find((a) => a.id === "ALL");
        if (account) {
            account.lastReconciledBalance = {
                balance: totalBalance,
                date: previousLastReconciledBalanceDate,
            };
        }
    }
}

class BankTransaction {
    dateDebit: 0;
    reconciliationStatus: any;
    suppliers;
    customers;
    users;
    accountingLines;
    constructor() {}

    get thirdPartyCompanies() {
        return (
            this.suppliers ||
            this.customers ||
            this.reconciliationStatus?.allocationSuggestion?.thirdPartyCompany?.name
        );
    }

    get allSuppliers() {
        return (
            this.suppliers ||
            (this.reconciliationStatus?.allocationSuggestion?.thirdPartyCompany?.name &&
                this.reconciliationStatus?.allocationSuggestion.accountableAccount.name.startsWith(
                    "401",
                ))
        );
    }

    get allCustomers() {
        return (
            this.customers ||
            (this.reconciliationStatus?.allocationSuggestion?.thirdPartyCompany?.name &&
                this.reconciliationStatus?.allocationSuggestion.accountableAccount.name.startsWith(
                    "411",
                ))
        );
    }

    get containtsThirdPartyCompanySuggestion() {
        return !!this.reconciliationStatus?.allocationSuggestion?.thirdPartyCompany;
    }

    get allUsers() {
        return this.users || this.reconciliationStatus?.allocationSuggestion?.user
            ? getUserDisplayName(this.reconciliationStatus?.allocationSuggestion?.user)
            : "";
    }

    get containsUserSuggestion() {
        return !!this.reconciliationStatus?.allocationSuggestion?.user;
    }

    get fullSuggestionDescription() {
        if (this.reconciliationStatus?.allocationSuggestion) {
            let description =
                this.reconciliationStatus.allocationSuggestion.accountableAccount.description;
            if (this.reconciliationStatus.allocationSuggestion.thirdPartyCompany) {
                description +=
                    " - " + this.reconciliationStatus.allocationSuggestion.thirdPartyCompany.name;
            } else if (this.reconciliationStatus.allocationSuggestion.user) {
                description +=
                    " - " + getUserDisplayName(this.reconciliationStatus.allocationSuggestion.user);
            } else if (this.reconciliationStatus.allocationSuggestion.bankAccount) {
                description +=
                    " - " + this.reconciliationStatus.allocationSuggestion.bankAccount.name;
            }
            return description;
        }
        return "";
    }

    get accountingLineTags() {
        if (!this.accountingLines) {
            return null;
        }
        const accountingLineTags = [];
        for (const al of this.accountingLines) {
            if (al.tag && !accountingLineTags.find((alt) => alt.id === al.tag.id)) {
                accountingLineTags.push(al.tag);
            }
            if (al.tags) {
                for (const tag of al.tags) {
                    if (!accountingLineTags.find((alt) => alt.id === tag.id)) {
                        accountingLineTags.push(tag);
                    }
                }
            }
        }
        return accountingLineTags.length ? accountingLineTags : null;
    }

    get labelTextColor() {
        return colors.grey.darken2;
    }

    // get transactionTypeText() {

    // }
    // get transactionTypeIcon() {
    //     const type = this.getTransactionType(bankTransaction);
    //     if (type) {
    //         const icon = this.dicoTransactionTypeIcon[type];
    //         if (icon) return icon;
    //     }
    //     return null
    // }
}

@Component
export class BankBase extends ComponentBase {
    @Ref() readonly asignButton!: any;

    amountTypes = [
        { value: "ALL", text: "Dépenses | Recettes" },
        { value: "EXPENSE", text: "Dépenses" },
        { value: "REVENUE", text: "Recettes" },
    ];

    payByList = [
        { value: "ALL", text: "Tous" },
        { value: "CREDIT_CARD", text: "Carte de crédit" },
        { value: "CASH", text: "Espèces" },
        { value: "CHEQUE", text: "Cheque" },
        { value: "TRANSFER", text: "Transfert" },
        { value: "LUNCHEON_VOUCHER", text: "Ticket restaurant" },
        { value: "HOLIDAY_VOUCHER", text: "Chèque vacances" },
        { value: "PAYPAL", text: "Paypal" },
        { value: "DISTRIBUTION_PLATFORM", text: "Plateforme de distribution" },
        { value: "CREDIT_NOTES_ISSUED", text: "Notes de crédit émises" },
        { value: "CREDIT_NOTES_RECOVERY", text: "Récupération des notes de crédit" },
        { value: "BANK_CHARGE", text: "Frais bancaires" },
        { value: "DIRECT_DEBIT", text: "Prélèvement automatique" },
    ];

    auditChecks = [
        { value: "", text: "Pas de contrôle" },
        { value: "CheckBTDoublon", text: "Doublon avec libellé" },
        { value: "CheckBTDoublonWithoutWording", text: "Doublon sans libellé" },
    ];

    dicoNatureTransactionType = {
        unknown: "UNKNOWN",
        bank: "BANK_CHARGE",
        order: "DIRECT_DEBIT",
        cash: "CASH",
        check: "CHEQUE", // chèque
        deposit: "", // espèce ou chèque
        payback: "BANK_CHARGE", // remboursement bancaire
        transfer: "TRANSFER",
        withdrawal: "", // retrait
        card: "CREDIT_CARD",
        summary_card: "",
        deferred_card: "CREDIT_CARD",
    };

    dicoTransactionTypeIcon = {
        UNKNOWN: "$fa-question-circle",
        CREDIT_CARD: "$fa-credit-card",
        CASH: "$fa-money-bill-wave",
        CHEQUE: "$fa-money-check",
        TRANSFER: "$fa-exchange-alt",
        LUNCHEON_VOUCHER: "",
        HOLIDAY_VOUCHER: "",
        PAYPAL: "",
        DISTRIBUTION_PLATFORM: "",
        CREDIT_NOTES_ISSUED: "",
        CREDIT_NOTES_RECOVERY: "",
        BANK_CHARGE: "",
        DIRECT_DEBIT: "",
    };

    dicoTransactionTypeBigram = {
        UNKNOWN: "IN",
        CREDIT_CARD: "CB",
        CASH: "LI",
        CHEQUE: "CH",
        TRANSFER: "VI",
        LUNCHEON_VOUCHER: "TR",
        HOLIDAY_VOUCHER: "CV",
        PAYPAL: "PP",
        DISTRIBUTION_PLATFORM: "LI",
        CREDIT_NOTES_ISSUED: "RA",
        CREDIT_NOTES_RECOVERY: "",
        BANK_CHARGE: "CO",
        DIRECT_DEBIT: "PR",
    };

    dicoAACategory = [
        { rootAA: "108", category: "Exploitant" },
        { rootAA: "20", category: "Immobilisations" },
        { rootAA: "21", category: "Immobilisations" },
        { rootAA: "26", category: "Immobilisations" },
        { rootAA: "28", category: "Amortissements" },
        { rootAA: "445510", category: "TVA" },
        { rootAA: "512000", category: "Banque" },
        { rootAA: "606", category: "Fournitures" },
        { rootAA: "607", category: "Achats" },
        { rootAA: "613200", category: "Loyer" },
        { rootAA: "613500", category: "Locations" },
        { rootAA: "615000", category: "Entretien" },
        { rootAA: "616000", category: "Assurance" },
        { rootAA: "621100", category: "Salaires" },
        { rootAA: "622000", category: "Services extérieurs" },
        { rootAA: "622600", category: "Honoraires" },
        { rootAA: "622700", category: "Services extérieurs" },
        { rootAA: "6257", category: "Réceptions" },
        { rootAA: "6251", category: "Déplacements" },
        { rootAA: "626", category: "Fournitures" },
        { rootAA: "627", category: "Frais bancaires" },
        { rootAA: "628", category: "Cotisations" },
        { rootAA: "635", category: "Impôts" },
        { rootAA: "641", category: "Salaires" },
        { rootAA: "645", category: "Social" },
        { rootAA: "646", category: "Social" },
        { rootAA: "658", category: "Frais divers" },
        { rootAA: "668", category: "Frais divers" },
        { rootAA: "681110", category: "Amortissements" },
        { rootAA: "681120", category: "Amortissements" },
        { rootAA: "706000", category: "Honoraires" },
        { rootAA: "706100", category: "Honoraires" },
        { rootAA: "706200", category: "Débours" },
        { rootAA: "758000", category: "Recettes diverses" },
        { rootAA: "768000", category: "Recettes financières" },
    ];

    // loadingStateNext = true;
    // loadingStateNextInQuery = true;
    firstRequestDone = false;

    nbrDates = 0;

    companyType = "";

    amountDebit = 0;
    amountCredit = 0;

    refreshKey = 0;

    graphDate = { startDate: "", endDate: "" };

    linkedDocumentsList = [];

    linkedDocumentsIds = [];

    linkedDocumentShown = "";

    onlyAccounting = false;

    activity: any;

    get isAccountingUser() {
        return this.currentUser.isAccountingUser;
    }

    get isAccountingClient() {
        return this.currentUser.isAccountingClient;
    }

    get isAuthorized() {
        const authorized = checkAccessRights(this.currentUser, "bankTransaction", "read");
        return authorized;
    }

    get isIncomingOutgoingPayment() {
        if (
            this.selectedCompany &&
            this.selectedCompany.profile &&
            this.selectedCompany.profile.accountingType === "INCOMING_OUTGOING_PAYMENT"
        ) {
            return true;
        }
        return false;
    }

    get isAccountingClientOfIOP() {
        return this.isAccountingClient && this.isIncomingOutgoingPayment;
    }
    // get previousLines() {
    //     this.loadingStateNext = false;
    //     if (this.pagination.page >= 4) return true;
    //     return false;
    // }

    get showStatus() {
        if (this.isIncomingOutgoingPayment) {
            return true;
        }
        return this.currentUser.profile.configuration.accounting.write;
    }

    get headers() {
        const headers: any[] = [];

        headers.push({ text: "Nature", value: "type" });

        headers.push(
            {
                text: "Libellé",
                value: "wordingOriginal",
                sortable: true,
                align: "center",
            },
            {
                text: "Montant",
                value: "amount",
                sortable: true,
                align: "center",
            },
            {
                text: "Date",
                value: "dateDebit",
                sortable: true,
                align: "center",
            },
        );

        if (this.showStatus) {
            if (!this.isIncomingOutgoingPayment) {
                // compta d'engagement
                headers.push(
                    {
                        text: "Statut",
                        value: "status",
                        sortable: false,
                        align: "center",
                    },
                    {
                        text: "Pièce",
                        value: "document",
                        sortable: false,
                        align: "center",
                    },
                );
            } else {
                // compta de trésorerie
                headers.push({
                    text: "Pièce",
                    value: "document",
                    sortable: false,
                    align: "center",
                });
            }
        }
        return headers;
    }

    progressCircularColor(value) {
        switch (value) {
            case 0:
                return "error";
            case 1:
                return "error";
            case 2:
                return "primary";
            case 3:
                return "green";
            case "INFORMATION_REQUEST":
                return "primary";
        }
    }

    get enableBudgetInsight() {
        if (!this.currentUser.profile.configuration.bankAccount.write) return false;
        return true;
    }

    get addBankAccountAvailable() {
        if (!this.enableBudgetInsight) return false;
        if (!this.currentUser.company.accountingFirm) {
            return true;
        }
        if (this.currentUser.company.id === this.selectedCompany.id) {
            return true;
        }
        return false;
    }

    get nextPageDisabled() {
        return (
            Math.ceil(this.pagination.totalItems / this.pagination.rowsPerPage) <=
            this.pagination.page
        );
    }

    get allReconciliationStatus() {
        if (this.currentUser.profile.configuration.accounting.write) {
            const allStatus = [
                "ALL",
                "JUSTIFIED",
                "PARTIALLY_JUSTIFIED",
                "NOT_JUSTIFIED",
                "LETTERING_PENDING_VALIDATION",
                "ALLOCATION_PENDING_VALIDATION",
                "ZTILL_RECEIPT",
                "SUPPLIER_DEFINED",
                "CUSTOMER_DEFINED",
                "SOCIAL",
                "STATE",
            ];
            if (this.legalForm !== "EI") {
                allStatus.push("CURRENT_ACCOUNT");
            } else {
                allStatus.push("OPERATOR_ACCOUNT");
            }
            allStatus.push("WITHOUT_INVOICE_TO_REPLACE");
            allStatus.push("EXPORTED");
            allStatus.push("INFORMATION_REQUEST");
            allStatus.push("DELETED");
            return allStatus;
        }
        return [
            "ALL",
            "JUSTIFIED",
            "PARTIALLY_JUSTIFIED",
            "NOT_JUSTIFIED",
            // "NOT_RECONCILIATED_CUSTOMER",
            "INFORMATION_REQUEST",
            "DELETED",
            // "RECONCILIATED_CUSTOMER"
        ];
    }

    // pagination = { sortBy: ["date"], descending: [true], page: 1, rowsPerPage: 10 };

    // loading = false;
    get loading() {
        if (!this.auditCheck) {
            if (this.$apollo.queries.filteredItems) {
                return this.$apollo.queries.filteredItems.loading;
            }
        } else if (this.$apollo.queries.auditCheckBTItems) {
            return this.$apollo.queries.auditCheckBTItems.loading;
        }
        return false;
    }

    pages = {
        start: 1,
        end: 1,
    };

    set sortBy(data) {
        // debugger
        this.pagination.sortBy = data.value;
        this.pagination.descending = data.descending;
    }

    get sortBy() {
        // debugger
        if (this.pagination && this.pagination.sortBy && this.pagination.sortBy.length) {
            const key = this.pagination.descending[0]
                ? this.pagination.sortBy[0] + "_desc"
                : this.pagination.sortBy[0] + "_asc";
            const head = this.sortByList.find((x) => x.key === key);
            return {
                key: key,
                value: this.pagination.sortBy[0],
                descending: this.pagination.descending[0],
                text: head.text,
            };
        }
        return null;
    }

    get sortByList() {
        return this.headers
            .filter((x) => x.sortable)
            .flatMap((x) => {
                return [
                    {
                        text: x.text + " croissant",
                        value: x.value,
                        descending: false,
                        key: x.value + "_asc",
                    },
                    {
                        text: x.text + " décroissant",
                        value: x.value,
                        descending: true,
                        key: x.value + "_desc",
                    },
                ];
            });
    }

    showRangePicker = false;

    @State2Way("updateBankTransactionAccount", "bankTransactionFilter.selectedAccount")
    selectedAccount!: string;

    @State2Way("updateBankTransactionFilter", "bankTransactionFilter")
    filter!: any;

    // @State2Way("updateBankTransactionShowFilterBox", state => state.bankTransactionFilter.showFilterBox)
    // showFilterBox!: boolean

    @State2Way("updateBankTransactionPagination", "bankTransactionFilter.pagination")
    pagination!: any;

    @State2Way("updateBankTransactionSearch", "bankTransactionFilter.search")
    search!: string;

    @State2Way("updateBankTransactionBeginDate", "bankTransactionFilter.date.begin")
    beginDate!: string;

    @State2Way("updateBankTransactionEndDate", "bankTransactionFilter.date.end")
    endDate!: string;

    @State2Way("updateBankTransactionAmountType", "bankTransactionFilter.amount.type")
    amountType!: string;

    @State2Way("updateBankTransactionMaxAmount", "bankTransactionFilter.amount.maximum")
    maxAmount!: string;

    @State2Way("updateBankTransactionMinAmount", "bankTransactionFilter.amount.minimum")
    minAmount!: string;

    @State2Way("updateBankTransactionPayBy", "bankTransactionFilter.payBy")
    payBy!: any;

    @State2Way("updateAccountingPeriod", "accountingPeriod.selectedAccountingPeriod")
    selectedAccountingPeriod!: any;

    @State2Way("updateBankTransactionAuditCheck", "bankTransactionFilter.auditCheck")
    auditCheck!: string;

    @State2Way("updateBankTransactionDisplayGraph", "bankTransactionFilter.displayGraph")
    displayGraph!: boolean;

    // @State2Way("updateBankTransactionShowNonReconcilied", "bankTransactionFilter.showNonReconcilied")
    // showNonReconcilied: boolean

    @State2Way(
        "updateBankTransactionReconciliationStatus",
        "bankTransactionFilter.reconciliationStatus",
    )
    reconciliationStatus: string;

    @State2Way("updateBankTransactionPrevNext", "bankTransactionFilter.prevNext")
    prevNext!: any;

    @State2Way("updateBankTransactionTags", "bankTransactionFilter.tags")
    bankTransactionTags!: any[];

    @State2Way("updateBankTransactionPartitionTagId", "bankTransactionFilter.partitionTagId")
    partitionTagId!: string;

    // @Watch("pagination", { deep: true })
    // test() {
    //     // debugger
    //     this.pagination;
    // }

    get filter_() {
        const result = {
            date: {
                start: this.beginDate,
                end: this.endDate,
            },
            price: [
                this.minAmount ? parseFloat(this.minAmount) : undefined,
                this.maxAmount ? parseFloat(this.maxAmount) : undefined,
            ],
            type: this.amountType,
            status: this.reconciliationStatus,
            text: this.search,
        };
        return result;
    }

    @Watch("search")
    @Watch("payBy")
    @Watch("reconciliationStatus")
    @Watch("amountType")
    @Watch("minAmount")
    @Watch("maxAmount")
    @Watch("beginDate")
    @Watch("endDate")
    @Watch("selectedAccount")
    @Watch("account")
    @Watch("auditCheck")
    @Watch("selectedCompany")
    @Watch("selectedCompany.id")
    // @("displayGraph")
    onChanged() {
        if (this.firstRequestDone) {
            this.firstRequestDone = false;
            this.pagination.page = 1;
            this.filteredItems = [];
            // this.waitingFilteredItems = []
            this.nbrDates = 0;
            this.auditCheckBTItems = [];
            this.limitMaxAmount = "";
            this.limitMinAmount = "";
        }
    }

    @Watch("selectedAccountingPeriod")
    onAccountingPeriodChanged(value, prevValue) {
        if (value) {
            if (!this.beginDate) {
                this.beginDate = moment(value.start).format("YYYY-MM-DD");
            } else {
                const mBegin = moment(this.beginDate);
                if (mBegin.isBefore(moment(value.start)) || mBegin.isAfter(moment(value.end))) {
                    this.beginDate = moment(value.start).format("YYYY-MM-DD");
                }
            }
            if (!this.endDate) {
                this.endDate = moment(value.end).format("YYYY-MM-DD");
            } else {
                const end = moment(this.endDate);
                if (end.isAfter(moment(value.end)) || end.isBefore(moment(value.start))) {
                    this.endDate = moment(value.end).format("YYYY-MM-DD");
                }
            }
        }
    }

    importWizard: boolean = false;

    activeSendChecks: boolean = false;

    freeTagFocused: boolean = false;

    onFreeTagFocus() {
        this.freeTagFocused = true;
    }

    onFreeTagBlur() {
        setTimeout(() => {
            this.freeTagFocused = false;
        }, 500);
    }

    // activeImportCSV: boolean = false;
    // activeImportScanBank: boolean = false;
    // activeImportPDF: boolean = false;

    // newDocument(data) {
    //     const test = +'2342.421'
    //     debugger
    //     const documentName = ""
    //     this.$apollo.mutate({
    //         mutation: CREATE_DOCUMENT,
    //         variables: {
    //             name: documentName,
    //             documentType: "BANK_STATEMENT",
    //             date: moment().format("YYYY-MM-DD"),
    //             companyId: this.selectedCompany.id,
    //             useForReconciliation: false,
    //             show: true,
    //             grossAmount: 0,
    //             grossAmountSigned: 0
    //         }
    //     })
    //     debugger
    // }

    // onFilesUploaded() {
    //     this.activeImportPDF = false
    // }

    onDateRangeChanged(data) {
        this.beginDate = data.begin;
        this.endDate = data.end;
    }

    // back() {
    // this.activeImportPDF = false
    // this.activeImportCSV = false
    // this.activeImportScanBank = false
    // this.importWizard = true
    // }
    finishMounted() {
        this.prevNext = {
            currentId: "",
            prevId: "",
            nextId: "",
        };
        this.onAccountingPeriodChanged(this.selectedAccountingPeriod, null);
    }

    bankAccountName(bankAccountName) {
        let name = bankAccountName.name;
        if (bankAccountName.number) {
            name += " (" + bankAccountName.number + ")";
        } else if (bankAccountName.iban) {
            name += " (" + bankAccountName.iban + ")";
        }
        return name;
    }

    formatDateMobile(date) {
        if (!date) return null;
        return moment(date, "DD-MM-YYYY").format("DD MMM");
    }

    newCommonFormatCurrency(value, options = undefined) {
        return newCommonFormatCurrency(value, undefined);
    }

    editBankTransactionDialog: boolean = false;

    openEditWording(bankTransaction) {
        // debugger
        this.selectedBankTransaction = bankTransaction;
        this.editBankTransactionDialog = true;
    }

    /**
     * Returns the formatted bank account name for display
     * @param bankAccount - The bank account
     * @returns The formatted bank account name
     */
    formatBankAccount(bankAccount): string {
        if (!bankAccount) {
            console.error("No bank account to format");
            return "";
        }

        let name = "";
        if (bankAccount.bank) {
            name = bankAccount.bank.name + " : ";
        }

        if (!bankAccount.lastReconciledBalance?.balance) {
            name += `${bankAccount.name} (-)`;
        } else {
            const date = bankAccount.lastReconciledBalance.date;
            const formattedCurrency = newCommonFormatCurrency(
                bankAccount.lastReconciledBalance.balance,
            );

            if (isDateOfToday(date)) {
                name += `${bankAccount.name} (${formattedCurrency})`;
            } else {
                const formattedDate = formatDateForUI(date);
                name += `${bankAccount.name} (${formattedCurrency} au ${formattedDate})`;
            }
        }

        return name;
    }

    // selectedWording = null
    selectedBankTransaction = null;

    formatStatus(status) {
        return this.$t("reconciliation.status." + status);
    }

    statusIndice(item) {
        if (item.status === "INFORMATION_REQUEST") {
            return item.status;
        }
        // 0: Rapprochement à commencer
        // 1: Rapprochement à terminer
        // 2: Rapprochement à vérifier
        // 3: Rapprochement validé
        if (item.reconciliationStatus) {
            if (item.reconciliationStatus.type === "ZTILL_RECEIPT") {
                return 3;
                // item.outlined = true;
            } else if (item.reconciliationStatus.type === "CURRENT_ACCOUNT") {
                return 3;
                // item.outlined = true;
            } else if (item.reconciliationStatus.type === "OPERATOR_ACCOUNT") {
                return 3;
                // item.outlined = true;
                // } else if (item.reconciliationStatus.type === "SOCIAL") {
                // item.outlined = false;
                // } else if (item.reconciliationStatus.type === "STATE") {
                // item.outlined = false;
                // } else if (item.reconciliationStatus.type === "MISCELLANEOUS") {
                // item.outlined = false;
            } else if (item.reconciliationStatus.allStatus.EDITION) {
                return 1;
                // item.color = "red";
                // item.outlined = false;
            } else if (item.reconciliationStatus.allStatus.PENDING_VALIDATION_FROM_CLIENT) {
                return 2;
                // item.outlined = false;
            } else if (item.reconciliationStatus.allStatus.PENDING_VALIDATION) {
                return 2;
                // item.outlined = false;
            } else if (isAccountingZero(item.reconciliationStatus.notReconciliatedAmountSigned)) {
                if (["ANALYSED", "UPLOADED"].includes(item.status)) {
                    return 0;
                }
                return 3;
                // item.color = "yellow";
                // item.outlined = true;
            } else if (item.reconciliationStatus.reconciliatedAmountSigned) {
                return 1;
                // item.color = "yellow";
                // item.outlined = true;
                // } else if (item.suppliers) {
                //     item.outlined = false;
                // } else if (item.customers) {
                //     item.outlined = false;
                // } else {
                //     item.outlined = false;
            }
        }
        return 0;
        // debugger;
        // item.outlined = false;
    }

    reconciliationColor(item) {
        if (item.statusIndice === "INFORMATION_REQUEST") {
            return "primary";
        }
        if (
            item.reconciliationStatus.allStatus.PENDING_VALIDATION ||
            item.reconciliationStatus.allStatus.PENDING_VALIDATION_FROM_CLIENT
        ) {
            return "magic";
        }
        if (this.isIncomingOutgoingPayment) {
            if (this.getLinkedDocumentId(item)) {
                return "secondary";
            }
            return "magic";
        }

        if (Math.abs(item.reconciliationStatus.toReconciliateAmountSigned) < 1e-2) {
            return "grey";
        }
        // return "secondary"
        if (isAccountingZero(item.reconciliationStatus.notReconciliatedAmountSigned)) {
            return "secondary";
        }
        if (isAccountingZero(item.reconciliationStatus.reconciliatedAmountSigned)) {
            if (this.isIncomingOutgoingPayment) {
                return "magic";
            }
            return "red";
        }

        // return "orange"
        return "secondary";
    }

    reconciliationIcon(item) {
        if (item.statusIndice === "INFORMATION_REQUEST") {
            return "$fa-comments";
        }
        if (
            item.reconciliationStatus.allStatus.PENDING_VALIDATION ||
            item.reconciliationStatus.allStatus.PENDING_VALIDATION_FROM_CLIENT
        ) {
            return "$fa-magic";
        }
        if (this.isIncomingOutgoingPayment) {
            if (this.getLinkedDocumentId(item)) {
                return "$oss-file";
            }
            return "$fa-file-upload";
        }
        if (Math.abs(item.reconciliationStatus.toReconciliateAmountSigned) < 1e-2) {
            return "$fa-check-circle";
        }
        if (isAccountingZero(item.reconciliationStatus.notReconciliatedAmountSigned)) {
            if (
                item.reconciliationStatus.accountableAccounts.find((aa) =>
                    aa.name?.startsWith("471000"),
                )
            ) {
                return "$oss-adjust";
            }
            return "$fa-check-circle";
        }
        if (isAccountingZero(item.reconciliationStatus.reconciliatedAmountSigned)) {
            return "$oss-circle-uncheck";
        }

        return "$oss-adjust";
    }

    reconciliationTooltip(item) {
        if (item.statusIndice === "INFORMATION_REQUEST") {
            return "Demande d'information";
        }
        if (
            item.reconciliationStatus.allStatus.PENDING_VALIDATION ||
            item.reconciliationStatus.allStatus.PENDING_VALIDATION_FROM_CLIENT
        ) {
            return "Lettrage à valider";
        }
        if (this.isIncomingOutgoingPayment) {
            if (this.getLinkedDocumentId(item)) {
                return "Voir le justificatif";
            }
            return "Justificatif à importer";
        }
        if (Math.abs(item.reconciliationStatus.toReconciliateAmountSigned) < 1e-2) {
            return "Aucun lettrage nécessaire";
        }
        if (isAccountingZero(item.reconciliationStatus.notReconciliatedAmountSigned)) {
            if (
                item.reconciliationStatus.accountableAccounts.find((aa) =>
                    aa.name?.startsWith("471000"),
                )
            ) {
                return "Lettré partiellement";
            }
            return "Lettré";
        }
        if (isAccountingZero(item.reconciliationStatus.reconciliatedAmountSigned)) {
            // if (this.isIncomingOutgoingPayment) {
            //     return "Justificatif à importer"
            // }
            return "Lettrage à commencer";
        }

        return "Lettrage à terminer";
    }

    linkedDocumentTooltip(item) {
        if (!item.linkedDocuments?.length) {
            return "Justificatif(s) à importer";
        } else {
            for (const doc of item.linkedDocuments) {
                if (doc.status !== "VALIDATED") {
                    return "Valider le justificatif";
                }
            }
            if (item.linkedDocuments?.length > 1) {
                return "Justificatifs liés";
            }
            return "Justificatif lié";
        }
    }

    linkedDocumentIcon(item) {
        if (!item.linkedDocuments?.length) {
            return "$fa-file-upload";
        }
        if (item.linkedDocuments?.length > 1) {
            return "$oss-duplicate";
        }
        return "$oss-file";
    }

    linkedDocumentColor(item) {
        if (!item.linkedDocuments?.length) {
            return "primary";
        } else {
            for (const doc of item.linkedDocuments) {
                if (doc.status !== "VALIDATED") {
                    return "magic";
                }
            }
            return "secondary";
        }
    }

    stateTooltip(item) {
        // 0: Rapprochement à commencer
        // 1: Rapprochement à terminer
        // 2: Rapprochement à vérifier
        // 3: Rapprochement validé

        if (item.status === "INFORMATION_REQUEST") {
            return item.chatStatus;
        }

        let statusAtt = "status";
        // if (this.selectedCompany.solution === "MANAGEMENT") {
        if (!this.isAccountingUser) {
            statusAtt = "customerStatus";
        }
        return this.$t("reconciliation." + statusAtt + "." + item.statusIndice);
    }

    get currentUser(): User {
        return this.$store.state.user;
    }

    get selectedCompany() {
        return this.$store.state.selected_company;
    }

    /**
     * Returns the selected or the most recent accounting period
     */
    get accountingPeriod() {
        return this.selectedAccountingPeriod ?? this.getMostRecentAccountingPeriodFromStore();
    }

    get selectedAccountingClosing() {
        return this.$store.state.accountingPeriod.selectedAccountingClosing?.id
            ? this.$store.state.accountingPeriod.selectedAccountingClosing
            : null;
    }

    get declareSalesInvoices() {
        if (
            this.selectedCompany.incomeCategory === "BNC" &&
            isNotSubjectToVAT(this.selectedCompany.vatSystem)
        ) {
            return false;
        }
        return true;
    }

    get legalForm() {
        return this.selectedCompany?.legalForm;
    }

    BTQueryVariables() {
        // conaccountId = this.accountIds, this.filter
        // debugger
        const filter = JSON.parse(JSON.stringify(this.filter));
        if (this.displayGraph) {
            if (this.graphDate.startDate && this.graphDate.endDate) {
                filter.date.begin = this.graphDate.startDate;
                filter.date.end = this.graphDate.endDate;
            }
        }
        const variables = getCommonBTQueryVariables(
            this.accountIds,
            this.selectedAccountingClosing,
            filter,
            this.selectedCompany.id,
            this.onlyAccounting,
        );
        variables.companyId = this.selectedCompany.id;
        variables.first = this.pagination.rowsPerPage > 0 ? this.pagination.rowsPerPage : 10;
        variables.skip = this.pagination.rowsPerPage * (this.pagination.page - 1);
        return variables;
    }

    @Apollo({
        query: gql`
            query BankAccountsQuery($companyId: ID!) {
                bankAccounts(where: { company: { id: $companyId } }) {
                    id
                    name
                    balance
                    initialSyncDone
                    currentError
                    number
                    # balances(orderBy: date_DESC, first: 1) {
                    #     id
                    #     date
                    #     balance
                    # }
                    bank {
                        id
                        name
                    }
                    type
                    budgetInsightId
                }
            }
        `,
        variables() {
            return {
                companyId: this.selectedCompany.id,
            };
        },
        skip() {
            return !this.isAuthorized || !this.selectedCompany?.id;
        },
        debounce: 500,
        specificResult({ data, loading, networkStatus }: any) {
            if (!loading) {
                if (data && data.bankAccounts) {
                    if (!data || !data.bankAccounts || data.bankAccounts.length === 0) {
                        this.accounts = [{ name: "", balance: 0, id: null }];
                        this.selectedAccount = "";
                    } else if (data.bankAccounts.length === 1) {
                        this.accounts = data.bankAccounts;
                        this.selectedAccount = data.bankAccounts[0].id;
                    } else {
                        for (const ba of data.bankAccounts) {
                            if (ba.balances?.length > 1) {
                                console.warn(
                                    "BankAccountsQuery 'balances(orderBy: date_DESC, first: 1) {' didn't ordered nor returns first 1 balance",
                                    ba,
                                );
                                ba.balances = orderBy(ba.balances, "date", "desc");
                            }
                        }
                        this.accounts = [
                            {
                                name: "Tous les comptes",
                                id: "ALL",
                            },
                            ...data.bankAccounts,
                        ];
                        if (this.selectedAccount && this.selectedAccount !== "ALL") {
                            if (!this.accounts.find((a) => a.id === this.selectedAccount)) {
                                this.selectedAccount = "ALL";
                            }
                        }
                        if (!this.selectedAccount) {
                            this.selectedAccount = "ALL";
                        }
                    }
                    if (this.$route.params.bankAccountId) {
                        if (
                            this.accounts.findIndex(
                                (a) => a.id === this.$route.params.bankAccountId,
                            )
                        ) {
                            this.selectedAccount = this.$route.params.bankAccountId;
                        }
                    }
                } else {
                    this.accounts = [];
                    this.selectedAccount = "";
                }
            }
        },
    })
    accounts: BankAccount[] = [];

    @Apollo({
        query: AUDIT_ITEMS,
        debounce: 500,
        variables() {
            this.auditItemsAccountBalance = [];

            return {
                companyId: this.selectedCompany.id,
                auditCheckKey: AuditCheckBankKey.BankReconciliation,
                accountingPeriodId: this.accountingPeriod?.id,
            };
        },
        skip() {
            return !this.selectedCompany?.id || !this.accounts?.length;
        },
        specificResult({ data, loading, networkStatus }: any) {
            if (!loading && data?.auditItems?.items?.[0]) {
                this.auditItemsAccountBalance = data.auditItems.items[0].bankAccounts;

                computeLastReconciledBalances.call(this);
            }
        },
    })
    auditItemsAccountBalance: any;

    showBankReconciliationDiscrepancyDialog = false;

    onAuditCheckAlertButtonClick() {
        this.showBankReconciliationDiscrepancyDialog = true;
    }

    /**
     * Returns the names of the accounts with unreconciled balances
     */
    get unreconciledBalancesAccountNames(): string[] {
        return this.auditItemsAccountBalance?.filter((i) => !i.validated)?.map((i) => i.name);
    }

    /**
     * Returns whether the ok button for bank reconciliation should be shown
     */
    get showReconciliationOkButton(): boolean {
        return (
            !this.showReconciliationDiscrepancyAlertButton && this.auditItemsAccountBalance?.length
        );
    }

    /**
     * Returns whether the alert button for reconciliation discrepancies should be shown
     */
    get showReconciliationDiscrepancyAlertButton(): boolean {
        if (!this.auditItemsAccountBalance?.length || !this.isAccountingUser) {
            return false;
        }

        return this.auditItemsAccountBalance.some((i) => !i.validated);
    }

    get account(): any {
        if (!this.selectedAccount) {
            return null;
        }
        if (!this.accounts.length) {
            return null;
        }
        return this.accounts.find((a) => a.id === this.selectedAccount);
    }

    checkClientAction(item) {
        if (this.isAccountingUser) {
            return;
        }
        if (isAccountingZero(item.reconciliationStatus.notReconciliatedAmountSigned)) {
            return;
        }
        if (this.isIncomingOutgoingPayment) {
            item.toJustify = true;
        }
        //return this.bankTransactionStatus === "INFORMATION_REQUEST"
    }

    analyseBTs(bankTransactions) {
        const output = [];
        for (let i = 0; i < bankTransactions.length; i++) {
            const item = Object.assign(new BankTransaction(), bankTransactions[i], {});
            output.push(item);

            this.computeReconciliationStatus(item);

            item.suppliers = "";
            if (
                item.reconciliationStatus &&
                item.reconciliationStatus.suppliers &&
                item.reconciliationStatus.suppliers.length
            ) {
                for (const tpCompany of item.reconciliationStatus.suppliers) {
                    item.suppliers = item.suppliers ? (item.suppliers += " - ") : "";
                    item.suppliers += tpCompany.shortName || tpCompany.name;
                }
            }
            item.customers = "";
            if (
                item.reconciliationStatus &&
                item.reconciliationStatus.customers &&
                item.reconciliationStatus.customers.length
            ) {
                for (const tpCompany of item.reconciliationStatus.customers) {
                    item.customers = item.customers ? (item.customers += " - ") : "";
                    item.customers += tpCompany.shortName || tpCompany.name;
                }
            }
            item.users = "";
            if (
                item.reconciliationStatus &&
                item.reconciliationStatus.users &&
                item.reconciliationStatus.users.length
            ) {
                for (const user of item.reconciliationStatus.users) {
                    item.users = item.users ? (item.users += " - ") : "";
                    item.users += getUserDisplayName(user);
                }
            }
            item.date = moment(item.dateDebit, "YYYY-MM-DD").format("DD-MM-YYYY");
            item.dateR = moment(item.dateRealization, "YYYY-MM-DD").format("DD-MM-YYYY");
            if (bankTransactions[i].amount >= 0) {
                bankTransactions[i].debit = "-";
                bankTransactions[i].credit = newCommonFormatCurrency(bankTransactions[i].amount);
            } else {
                const debit = -bankTransactions[i].amount;
                bankTransactions[i].debit = newCommonFormatCurrency(debit);
                bankTransactions[i].credit = "-";
            }
            let statusAtt = "status";
            // if (this.selectedCompany.solution === "MANAGEMENT") {
            if (!this.isAccountingUser) {
                statusAtt = "customerStatus";
            }
            item.statusIndice = this.statusIndice(item);
            if (item.reconciliationStatus) {
                // debugger
                if (item.reconciliationStatus.type === "ZTILL_RECEIPT") {
                    item.allocation = this.$t(
                        "reconciliation." + statusAtt + "." + item.reconciliationStatus.type,
                    );
                    item.outlined = true;
                } else if (item.reconciliationStatus.type === "CURRENT_ACCOUNT") {
                    item.allocation = this.$t(
                        "reconciliation." + statusAtt + "." + item.reconciliationStatus.type,
                    );
                    item.outlined = true;
                } else if (item.reconciliationStatus.type === "OPERATOR_ACCOUNT") {
                    item.allocation = this.$t(
                        "reconciliation." + statusAtt + "." + item.reconciliationStatus.type,
                    );
                    item.outlined = true;
                } else if (item.reconciliationStatus.type === "SOCIAL") {
                    item.allocation = this.$t(
                        "reconciliation." + statusAtt + "." + item.reconciliationStatus.type,
                    );
                    item.outlined = false;
                } else if (item.reconciliationStatus.type === "STATE") {
                    item.allocation = this.$t(
                        "reconciliation." + statusAtt + "." + item.reconciliationStatus.type,
                    );
                    item.outlined = false;
                } else if (item.reconciliationStatus.type === "MISCELLANEOUS") {
                    item.allocation = this.$t(
                        "reconciliation." + statusAtt + "." + item.reconciliationStatus.type,
                    );
                    item.outlined = false;
                } else if (item.suppliers) {
                    item.allocation = this.$t("reconciliation." + statusAtt + ".SUPPLIER_DEFINED");
                    item.outlined = false;
                } else if (item.customers) {
                    item.allocation = this.$t("reconciliation." + statusAtt + ".CUSTOMER_DEFINED");
                    item.outlined = false;
                } else if (item.reconciliationStatus.type === "SALES_INVOICE") {
                    item.allocation = this.$t(
                        "reconciliation." + statusAtt + "." + item.reconciliationStatus.type,
                    );
                    item.outlined = false;
                } else if (item.reconciliationStatus.type === "PURCHASE_INVOICE") {
                    item.allocation = this.$t(
                        "reconciliation." + statusAtt + "." + item.reconciliationStatus.type,
                    );
                    item.outlined = false;
                }
                // }  else if (item.reconciliationStatus.allStatus.EDITION) {
                //     item.allocation = this.$t("reconciliation." + statusAtt + ".EDITION");
                //     item.outlined = false;
                // } else if (item.reconciliationStatus.allStatus.PENDING_VALIDATION_FROM_CLIENT) {
                //     item.allocation = this.$t("reconciliation." + statusAtt + ".PENDING_VALIDATION_FROM_CLIENT");
                //     item.outlined = false;
                // } else if (item.reconciliationStatus.allStatus.PENDING_VALIDATION) {
                //     item.allocation = this.$t("reconciliation." + statusAtt + ".PENDING_VALIDATION");
                //     item.outlined = false;
                // } else if (Math.abs(item.reconciliationStatus.notReconciliatedAmountSigned) < 1.e-3) {
                //     item.allocation = this.$t("reconciliation." + statusAtt + ".VALIDATED");
                //     item.outlined = true;
                // } else if (item.reconciliationStatus.reconciliatedAmountSigned) {
                //     item.allocation = this.$t("reconciliation." + statusAtt + ".PARTIALLY_VALIDATED");
                //     item.outlined = true;
                else {
                    item.allocation = this.$t("reconciliation." + statusAtt + ".NOT_DEFINED");
                    item.outlined = false;
                    this.checkClientAction(item);
                }
            } else {
                debugger;
                item.allocation = this.$t("reconciliation." + statusAtt + ".NOT_DEFINED");
                // item.allocation = "A Rapprocher";
                item.color = "red";
                item.outlined = false;
                this.checkClientAction(item);
            }
            getChatStatus(this, item, item.chat);
            this.finishReadItem(item);
        }
        return output;
    }

    finishReadItem(item) {}

    get accountIds() {
        return this.account && this.account.id && this.account.id !== "ALL"
            ? [this.account.id]
            : this.accounts.filter((e) => e.id && e.id !== "ALL").map((e) => e.id);
    }

    getQuery() {
        return BANKTRANSACTIONS_FILTER_QUERY;
    }

    @Apollo({
        query() {
            return this.getQuery();
        },
        variables() {
            let variables = this.BTQueryVariables();
            // this.loadingStateNextInQuery = this.loadingStateNext;
            return variables;
        },
        fetchPolicy: "network-only",
        debounce: 500,
        skip() {
            return (
                !this.isAuthorized ||
                !this.selectedCompany?.id ||
                !this.accounts.length ||
                !!this.auditCheck ||
                this.showWithoutInvoiceDialog ||
                this.showZTillDialog ||
                this.autoSalesReconciliationDialog ||
                this.thirdPartyDialog
            );
        },
        specificResult({ data, loading, networkStatus }: any) {
            // this.loading = loading;
            if (!loading && data) {
                if (data.bankTransactions) {
                    if (data.bankTransactions.length) {
                        if (!data.bankTransactions[0].date) {
                            this.selected = [];
                        }
                    }
                    this.firstRequestDone = true;
                    // debugger
                    const bankTransactions = this.analyseBTs(data.bankTransactions);
                    // bankTransactions);

                    // if (this.isAccountingUser) {
                    if (!this.$vuetify.display.mobile) {
                        this.filteredItems = bankTransactions;
                    } else {
                        // else if (this.loadingStateNextInQuery) {
                        this.nextAction(bankTransactions);
                    }
                    // else {
                    //     this.filteredItems.push(...bankTransactions);
                    // }
                    // debugger
                    this.pagination.totalItems = data.bankTransactionAggregate.count;
                    this.amountDebit = data.bankTransactionAggregate.amountDebit;
                    this.amountCredit = data.bankTransactionAggregate.amountCredit;
                    // console.log('Taille in DB :', this.pagination.totalItems)
                    // console.log('Taille Liste : ', this.items.length - this.nbrDates)
                } else {
                    this.selected = [];
                    this.filteredItems = [];
                    this.pagination.totalItems = 0;
                    this.amountDebit = 0;
                    this.amountCredit = 0;
                }
            } else {
                this.selected = [];
                this.filteredItems = [];
                this.pagination.totalItems = 0;
            }
        },
    })
    filteredItems: any[] = [];

    @Apollo({
        query: MIN_MAX_BANK_TRANSACTIONS,
        variables() {
            return getBTMinMaxQueryVariables(
                this.accountIds,
                this.selectedAccountingClosing,
                this.filter,
                this.selectedCompany.id,
                this.onlyAccounting,
            );
        },
        fetchPolicy: "network-only",
        debounce: 500,
        skip() {
            return (
                !this.isAuthorized ||
                !this.selectedCompany?.id ||
                !this.accounts.length ||
                !!this.auditCheck ||
                this.showWithoutInvoiceDialog ||
                this.showZTillDialog ||
                this.autoSalesReconciliationDialog ||
                this.thirdPartyDialog
            );
        },
        specificResult({ data, loading, networkStatus }: any) {
            if (!loading && data) {
                if (data.maxPrice && data.maxPrice.length) {
                    this.limitMaxAmount = data.maxPrice[0].amount;
                } else {
                    this.limitMaxAmount = "";
                }

                if (data.minPrice && data.minPrice.length) {
                    this.limitMinAmount = data.minPrice[0].amount;
                } else {
                    this.limitMinAmount = "";
                }
            }
        },
    })
    limitMaxAmount: any = "";

    limitMinAmount: any = "";

    @Apollo({
        query: AUDIT_BT_ITEMS,
        variables() {
            return {
                companyId: this.selectedCompany.id,
                auditCheck: this.auditCheck,
                // accountingPeriodId: this.$route.params.accountingPeriodId,
                date_gte: this.beginDate || undefined,
                date_lte: this.endDate || undefined,
            };
        },
        debounce: 500,
        skip() {
            return (
                !this.isAuthorized ||
                !this.selectedCompany?.id ||
                !this.auditCheck ||
                this.showWithoutInvoiceDialog ||
                this.showZTillDialog ||
                this.autoSalesReconciliationDialog ||
                this.thirdPartyDialog
            );
        },
        specificResult({ data, loading, networkStatus }: any) {
            if (!loading) {
                this.selected = [];
                this.filteredItems = [];
                this.pagination.totalItems = 0;
                this.amountDebit = 0;
                this.amountCredit = 0;

                if (data && data.auditBTItems) {
                    // console.log(data.auditItems);
                    const bts = data.auditBTItems;
                    bts.sort((a, b) => {
                        if (a.dateDebit < b.dateDebit) return 1;
                        if (a.dateDebit > b.dateDebit) return -1;
                        if (a.amount < b.amount) return 1;
                        if (a.amount > b.amount) return -1;
                        return 0;
                    });
                    // for (const doublon of data.auditItems.items) {
                    //     for (const bt of doublon.bts) {
                    //         this. auditCheckBTItems.push(bts);
                    //     }
                    // }

                    this.auditCheckBTItems = this.analyseBTs(bts);
                    this.pagination.totalItems = this.auditCheckBTItems.length;
                } else {
                    this.auditCheckBTItems = [];
                }
            }
        },
    })
    auditCheckBTItems = [];

    get items() {
        // debugger
        if (!this.auditCheck) {
            return this.filteredItems;
        }
        return this.auditCheckBTItems;
    }

    // ,
    // data => data.bankTransactions
    // )

    nextAction(data) {
        console.log(this.pages);
        if (data.length) {
            this.pages.end++;

            let prevDate = "";
            let prevHeader;
            if (this.filteredItems.length) {
                const lastItem = this.filteredItems[this.filteredItems.length - 1];
                prevDate = moment.utc(lastItem.dateDebit, "YYYY-MM-DD").format("MMMM YYYY");
                for (let i = this.filteredItems.length - 1; i >= 0; i--) {
                    if (this.filteredItems[i].header) {
                        prevHeader = this.filteredItems[i];
                    }
                }
            }
            for (const item of data) {
                const date = moment(item.dateDebit, "YYYY-MM-DD").format("MMMM YYYY");
                this.filteredItems.push(item);
                prevDate = date;
            }
        }
    }

    previousAction(data) {
        console.log(this.pages);
        this.pages.start--;
        this.pages.end--;
        if (this.filteredItems.length >= 30) {
            this.filteredItems.splice(19, 10);
            if (document.documentElement) {
                document.documentElement.scrollTop = document.documentElement.scrollTop / 2;
            }
        }
        this.filteredItems.unshift(...data);
    }

    dialog: boolean = false;

    get defaultFilter() {
        return defaultBankTransactionFilter;
    }

    isFiltered(x: any, y: any): boolean {
        const optFields = ["showFilterBox", "totalItems"];
        if (x && y && typeof x === "object" && typeof y === "object") {
            return (
                Object.keys(x).length === Object.keys(y).length &&
                Object.keys(x).reduce((isEqual, key) => {
                    if (optFields.includes(key)) {
                        return isEqual;
                    }
                    return isEqual && this.isFiltered(x[key], y[key]);
                }, true)
            );
        }
        return x === y;
    }

    clearAllFilters() {
        this.filter = JSON.parse(JSON.stringify(this.defaultFilter));
    }

    onItemClick(item) {
        if (this.freeTagFocused) {
            return;
        }
        if (item.status === "DELETED") {
            return;
        }
        //
        // accounting
        //
        if (this.isAccountingUser) {
            openBankTransaction(this.$router, this.currentUser, this.selectedCompany.id, item);
            return;
        }
        //
        // client
        //
        this.onReconciliationClick(item, true);
    }

    onReconciliationClick(item, showBankInfo: boolean = false) {
        if (item.statusIndice === "INFORMATION_REQUEST") {
            return openBankTransaction(
                this.$router,
                this.currentUser,
                this.selectedCompany.id,
                item,
            );
        }
        if (this.isIncomingOutgoingPayment) {
            if (
                item.reconciliationStatus.allStatus.PENDING_VALIDATION ||
                item.reconciliationStatus.allStatus.PENDING_VALIDATION_FROM_CLIENT
            ) {
                openBankTransaction(this.$router, this.currentUser, this.selectedCompany.id, item);
            } else {
                this.selectedBankTransaction = item;
                if (!showBankInfo && this.getLinkedDocumentId(item)) {
                    this.bncDocumentDialog = true;
                } else {
                    this.showBncAssignDocumentDialog = true;
                }
            }
        } else {
            openBankTransaction(this.$router, this.currentUser, this.selectedCompany.id, item);
        }
    }

    onLinkedDocumentClick(item) {
        this.selectedBankTransaction = item;
        if (item.linkedDocuments?.length) {
            this.linkedDocumentsList = item.linkedDocuments;
            this.linkedDocumentsIds = item.linkedDocuments.map((ld) => ld.id);
            this.linkedDocumentShown = this.linkedDocumentsIds[0];
            this.documentDialog = true;
        } else {
            this.showBncAssignDocumentDialog = true;
        }
    }

    previousDisabled() {
        return this.linkedDocumentsIds.indexOf(this.linkedDocumentShown) <= 0;
    }

    previous() {
        if (this.linkedDocumentsIds.indexOf(this.linkedDocumentShown) > 0) {
            this.linkedDocumentShown =
                this.linkedDocumentsIds[
                    this.linkedDocumentsIds.indexOf(this.linkedDocumentShown) - 1
                ];
        }
    }

    nextDisabled() {
        return (
            this.linkedDocumentsIds.indexOf(this.linkedDocumentShown) + 1 >=
            this.linkedDocumentsIds.length
        );
    }

    next() {
        if (
            this.linkedDocumentsIds.indexOf(this.linkedDocumentShown) + 1 <
            this.linkedDocumentsIds.length
        ) {
            this.linkedDocumentShown =
                this.linkedDocumentsIds[
                    this.linkedDocumentsIds.indexOf(this.linkedDocumentShown) + 1
                ];
        }
    }

    candidateAccountingLines(item) {
        if (item) {
            let uniqueAL = null;
            const accountingLines = [];
            for (const al of item.accountingLines) {
                if (al.document) {
                    return [];
                }
                if (al.accountableAccount.name.startsWith("512")) {
                    if (!uniqueAL) {
                        uniqueAL = al;
                    }
                } else if (al.accountableAccount.name.startsWith("471000")) {
                    uniqueAL = al;
                } else {
                    accountingLines.push(al);
                }
            }
            if (!accountingLines.length && uniqueAL) {
                accountingLines.push(uniqueAL);
            }
            return accountingLines;
        }
        return [];
    }

    isAlreadyAllocated(item) {
        if (
            item.accountingLines.length === 1 &&
            item.accountingLines[0].accountableAccount.name.startsWith("512")
        ) {
            return false;
        }
        if (
            this.candidateAccountingLines(item).length === 1 &&
            (this.candidateAccountingLines(item)[0].accountableAccount.name.startsWith("471000") ||
                this.candidateAccountingLines(item)[0].accountableAccount.name.startsWith("512"))
        ) {
            return false;
        }
        return true;
    }

    async onAccountableAccountClick(item) {
        if (item.statusIndice === "INFORMATION_REQUEST") {
            openBankTransaction(this.$router, this.currentUser, this.selectedCompany.id, item);
        }
        if (this.isIncomingOutgoingPayment) {
            // if (item.reconciliationStatus.allStatus.PENDING_VALIDATION || item.reconciliationStatus.allStatus.PENDING_VALIDATION_FROM_CLIENT) {
            //     openBankTransaction(this.$router, this.currentUser, this.selectedCompany.id, item)
            // }
            if (this.isAccountingClient) {
                if (!this.isAlreadyAllocated(item) && this.asignButton) {
                    this.toAsignIndividualAccount = true;
                    this.selected = [item];
                    await this.$nextTick();
                    this.asignButton.$el.click();
                    return;
                }
            }
            this.selectedBankTransaction = item;
            this.showBncAssignDocumentDialog = true;
        } else {
            openBankTransaction(this.$router, this.currentUser, this.selectedCompany.id, item);
        }
    }

    showBncAssignDocumentDialog: boolean = false;

    toAsignIndividualAccount: boolean = false;

    bankTransactionIdOpen = "";

    get subDefaultParams() {
        if (this.isAccountingClientOfIOP) {
            return false;
        }
        return true;
    }

    // get openLine() {
    //     return {
    //         companyId: this.selectedCompany.id,
    //         bankTransactionId: this.bankTransactionIdOpen,
    //         inbeddedMode: true,
    //     };
    // }

    dialogDeletePermanentlySelectedBTs: boolean = false;

    async deleteBankTransactions(permanentely: boolean = false) {
        const result = await this.$apollo.mutate({
            mutation: DELETE_BANK_TRANSACTIONS,
            variables: {
                ids: this.selected.map((x) => x.id),
                companyId: this.selectedCompany.id,
                lightDelete: !permanentely,
            },
        });
        if (permanentely) {
            this.dialogDeletePermanentlySelectedBTs = false;
        } else {
            this.showDeleteBankTransactionsDialog = false;
        }

        this.refresh();
        debugger;
        // this.showDeleteBankTransactionsDialog = false
        // this.notDeleted = result.data.deleteBankTransactions.notDeleted
        // this.listOfUnacceptedDates = result.data.deleteBankTransactions.bankDates
        // if(this.notDeleted.length) {
        //     this.showNoDeletedBankTransactionsDialog = true
        // }
        if (result.data?.deleteBankTransactions?.notificationId) {
            this.deleteId = result.data.deleteBankTransactions.notificationId;
        }
    }

    deleteId: string = "";
    stepData_ = null;

    editedIndex: number = -1;

    editedBankTransaction: any = {
        reconciliation: {
            id: "",
            status: "",
        },
    };

    // defaultEditedBankTransaction: any = {
    //     reconciliation: {
    //         id: "",
    //         status: ""
    //     }
    // };

    btColor(bt) {
        if (!bt) return "";
        if (bt.header) return "primary";
        if (!bt.amount) return "grey";
        if (bt.amount < 0) return "new_red";
        return "secondary";
    }

    btIcon(bt) {
        if (!bt) return "";
        if (bt.header) return "date_range";
        if (!bt.amount) return "";
        if (bt.amount < 0) return "trending_down";
        return "trending_up";
    }

    openImportCSVWizard() {
        debugger;
        this.importWizard = false;
        // this.activeImportCSV = true
    }

    // get editBTAmountLabel(): string {
    //     if (this.editedBankTransaction) {
    //         if (this.editedBankTransaction.amount < 0) {
    //             return "Credit";
    //         }
    //         return "Debit";
    //     }
    //     return "";
    // }

    // get editBTDate(): string {
    //     if (
    //         this.editedBankTransaction &&
    //         this.editedBankTransaction.dateRealization
    //     ) {
    //         // BUG FIX BI DATE
    //         return moment
    //             .utc(this.editedBankTransaction.dateRealization)
    //             .format("DD-MM-YYYY"); // BUG FIX BI DATE
    //     }
    //     return "";
    // }

    // get editBTAmount(): string {
    //     if (this.editedBankTransaction) {
    //         return Math.abs(this.editedBankTransaction.amount).toFixed(2);
    //     }
    //     return "0.00";
    // }

    // get editDocumentAmountLabel(): string {
    //     if (this.editedBankTransaction) {
    //         if (this.editedBankTransaction.amount < 0) {
    //             return "Debit";
    //         }
    //         return "Credit";
    //     }
    //     return "";
    // }

    async reconciliateCompany() {
        // this.loading = true;
        const mutation = RECONCILIATE_COMPANY;
        const result = await this.$apollo.mutate({
            mutation: mutation,
            variables: {
                companyId: this.selectedCompany.id,
            },
        });
        debugger;

        if (result && result.data && result.data.reconciliateCompany) {
            console.log("reconciliateCompany OK");
            this.refresh();
        }
        // else {
        // this.loading = false;
        // }
    }

    async updateBankAccount() {
        // debugger;
        // const baResult = await this.$apollo.query({
        //     query: BANK_ACCOUNTS,
        //     variables: {
        //         companyId: this.selectedCompany.id,
        //     }
        // });
        // debugger;
        // if (baResult && baResult.data && baResult.data.bankAccounts && baResult.data.bankAccounts.length) {
        // this.loading = true;
        if (this.account && this.account.id) {
            const mutation = AUTO_IMPORT_BANK_TRANSACTIONS;
            // baResult.data.bankAccounts.forEach(async (e) => {
            // console.log(e.id, e.name)
            const result = await this.$apollo.mutate({
                mutation: mutation,
                variables: {
                    bankAccountId: this.account.id,
                },
            });
        }
        // });

        // this.loading = false
        this.refresh();
        // }
    }

    connectLoading: boolean = false;

    async connectClicked() {
        this.connectLoading = true;
        await connectBank(this.$apollo, this.selectedCompany.id);
        this.connectLoading = false;
    }

    refresh() {
        if (!this.auditCheck) {
            if (this.$apollo.queries.filteredItems) {
                this.$apollo.queries.filteredItems.refetch();
            }
        } else if (this.$apollo.queries.auditCheckBTItems) {
            this.$apollo.queries.auditCheckBTItems.refetch();
        }
        this.$apollo.queries.accounts?.refetch();
    }

    addCheckboxSelection(item) {
        if (!item) {
            debugger;
            return false;
        }
        if (item.status === "DELETED") {
            return false;
        }
        // if (item.amount <= 0) return false;
        if (item.reconciliationStatus) {
            // if (item.reconciliationStatus.type) {
            //     return false;
            // }
            if (item.reconciliationStatus.allocatedAmountSigned === 0) {
                return true;
            }
            return false;
        }
        return true;
    }

    addCheckboxSelectionIfNotAllocatedAmount(item) {
        if (!item) {
            debugger;
            return false;
        }
        if (item?.status === "DELETED") {
            return false;
        }
        // if (item.amount <= 0) return false;
        if (item?.reconciliationStatus) {
            // if (item.reconciliationStatus.type) {
            //     return false;
            // }
            if (item.reconciliationStatus.notAllocatedAmountSigned) {
                return true;
            }
            return false;
        }
        return true;
    }

    selected = [];

    get selectedAmountDebit() {
        let total = 0;
        for (const bt of this.selected) {
            if (bt.amount > 0) total += bt.amount;
        }
        return total;
    }

    get selectedAmountCredit() {
        let total = 0;
        for (const bt of this.selected) {
            if (bt.amount < 0) total += bt.amount;
        }
        return total;
    }

    //
    // Without Invoice
    //

    withoutInvoiceVAT: number = -1;

    showDeleteBankTransactionsDialog = false;
    showNoDeletedBankTransactionsDialog = false;

    notDeleted = [];
    listOfUnacceptedDates = [];

    onWithoutInvoiceClick() {
        this.showWithoutInvoiceDialog = !this.showWithoutInvoiceDialog;
        this.withoutInvoiceAccountableAccount = null;
    }

    get disabledWithoutInvoice(): boolean {
        return this.withoutInvoiceBTS.length === 0;
    }

    showWithoutInvoiceDialog: boolean = false;
    withoutInvoiceLoading: boolean = false;

    withoutInvoiceActions: any = {
        action: new DialogAction(),
    };

    get withoutInvoiceBTS() {
        const bts = [];
        for (const item of this.selected) {
            if (this.addCheckboxSelection(item)) {
                bts.push(item);
            } else {
                // debugger;
                this.withoutInvoiceLoading = false;
                this.showWithoutInvoiceDialog = false;
                return [];
            }
        }
        return bts;
    }

    resetIOPAccountsVariables() {
        if (this.toAsignIndividualAccount) {
            this.toAsignIndividualAccount = false;
            this.selected = [];
        }
    }

    closeAccountableAccount() {
        this.resetIOPAccountsVariables();
        debugger;
    }

    selectAccountableAccount(data) {
        this.withoutInvoiceAccountableAccount = data.accountableAccount;
    }

    withoutInvoiceAccountableAccount: AccountableAccount = null;

    // -------------
    //  Delete BT
    // -------------

    get disabledDeleteBT(): boolean {
        for (const item of this.selected) {
            if (item.accountingClosing || item.status === "DELETED") {
                return true;
            }
        }
        return this.disabledWithoutInvoice;
    }

    // -------------
    //  Restore BT
    // -------------

    canBeRestoredTooltipMessages: Array<string> = [];

    get disabledRestoreBT(): boolean {
        this.canBeRestoredTooltipMessages.length = 0;
        if (!this.selected.length) {
            return true;
        }

        if (!this.currentUser.profile.configuration.document.write) {
            this.canBeRestoredTooltipMessages.push("Vous êtes en mode lecture seule");
        }

        const someNotDeletedBT = this.selected.some((s) => s.status !== "DELETED");
        if (someNotDeletedBT) {
            this.canBeRestoredTooltipMessages.push(
                "Des écritures bancaires n'ont pas le status 'Supprimé'",
            );
        }

        const someBlockedSelected = this.selected.some((s) =>
            this.btIsInsideAccountingClosing(s.dateDebit),
        );
        if (someBlockedSelected) {
            this.canBeRestoredTooltipMessages.push(
                "Des écritures bancaires sont verrouillées par une période comptable clôturée ou par une situation",
            );
        }

        if (this.canBeRestoredTooltipMessages.length) return true;

        return false;
    }

    btIsInsideAccountingClosing(date) {
        return isInsideAccountingClosing(this.$store, date);
    }

    async restoreBankTransactions() {
        if (this.selected?.length) {
            await this.$apollo.mutate({
                mutation: gql`
                    mutation ($ids: [ID!]!) {
                        updateManyBankTransactions(
                            where: { id_in: $ids }
                            data: { status: ANALYSED }
                        ) {
                            count
                        }
                    }
                `,
                variables: {
                    ids: this.selected.map((s) => s.id),
                },
            });
            this.refresh();
        }
    }
    // -------------
    //  Third Party
    // -------------

    thirdPartyDialog = false;

    disabledThirdPartyCompany() {
        if (!this.selected.length) {
            return true;
        }
        for (const item of this.selected) {
            if (
                (item.reconciliationStatus &&
                    (item.reconciliationStatus.notAllocatedAmountSigned === 0 ||
                        (item.accountingLines.length > 1 &&
                            item.accountingLines.every(
                                (al) => al.id && !al.accountableAccount?.name?.startsWith("512"),
                            )))) ||
                item.accountingItemImport ||
                item.status === "DELETED"
            ) {
                return true;
            }
        }
        return false;
    }

    get isIopAccountableAccountDisabled() {
        if (!this.selected.length) {
            return true;
        }

        return this.selected.some(
            (item) =>
                (item.reconciliationStatus &&
                    (item.reconciliationStatus.notAllocatedAmountSigned === 0 ||
                        (item.accountingLines.length > 1 &&
                            item.accountingLines.every(
                                (al) => al.id && !al.accountableAccount?.name?.startsWith("512"),
                            )))) ||
                item.status === "DELETED",
        );
    }

    onThirdPartyAdded() {
        this.thirdPartyDialog = false;
        this.selected = [];
    }

    // -----------
    //  Auto Sale
    // -----------

    autoSalesReconciliationDialog: boolean = false;
    autoSalesReconciliationLoading: boolean = false;
    autoSalesActions: any = {
        action: new DialogAction(),
    };

    get disabledAutoSalesReconciliation() {
        let count = 0;
        for (const item of this.selected) {
            if (this.addCheckboxSelection(item)) {
                if (item.amount < 0) return true;
                count++;
            } else {
                return true;
            }
        }
        return count === 0;
    }

    async onAutoSalesCreate(data) {
        this.autoSalesReconciliationLoading = true;
        const bts = [];
        for (const item of this.selected) {
            if (this.addCheckboxSelection(item)) {
                bts.push(item);
            } else {
                return;
            }
        }
        if (bts.length && this.selectedCompany.id && this.selectedCompany) {
            await createReconciliationsWithSalesAuto(
                this.$apollo,
                this.selectedCompany.id,
                bts,
                data,
            );
            this.refresh();
        }
        this.autoSalesReconciliationLoading = false;
        this.autoSalesReconciliationDialog = false;
    }

    //
    // Z till
    //
    showZTillDialog: boolean = false;
    zTillLoading: boolean = false;

    get disabledZTillReconciliation() {
        let count = 0;
        if (!this.selected || !this.selected.length) {
            return true;
        }
        for (const item of this.selected) {
            if (this.addCheckboxSelectionIfNotAllocatedAmount(item)) {
                if (item.amount < 0) return true;
                count++;
            } else {
                return true;
            }
        }
        return count === 0;
    }

    get showZTillReconciliation() {
        if (!this.declareSalesInvoices) return false;
        return true;
    }

    async executeZTillReconciliation() {
        this.zTillLoading = true;
        const bts = [];
        for (const item of this.selected) {
            if (this.addCheckboxSelectionIfNotAllocatedAmount(item)) {
                if (item.amount < 0) {
                    continue;
                }
                bts.push(item);
            } else {
                debugger;
                this.zTillLoading = false;
                this.showZTillDialog = false;
                return;
            }
        }
        if (bts.length && this.selectedCompany.id && this.selectedCompany) {
            await linkBankTransactionWithCashBox(this.$apollo, bts);
            this.refresh();
        }
        this.zTillLoading = false;
        this.showZTillDialog = false;
    }

    showDeallocateDialog = false;
    deallocateReconciliationLoading = false;
    deallocationChoice = "SELECTION";

    get showDeallocationChoice() {
        if (!this.enableDeallocationSelection || !this.enableDeallocationOnWhere) {
            return false;
        }
        return true;
    }

    get enableDeallocationOnWhere() {
        if (!this.filter.date.begin) {
            return false;
        }
        if (isInsideClosedAccountingPeriod(this.$store, this.filter.date.begin)) {
            return false;
        }
        if (!this.filter.date.end) {
            return false;
        }
        if (isInsideClosedAccountingPeriod(this.$store, this.filter.date.end)) {
            return false;
        }
        return true;
    }

    get enableDeallocationSelection() {
        if (!this.selected.length) {
            return false;
        }
        let count = 0;
        for (let bt of this.selected) {
            if (bt.status === "DELETED") {
                continue;
            }
            if (isInsideClosedAccountingPeriod(this.$store, bt.dateDebit)) {
                return false;
            }
            const als = bt.accountingLines;
            if (!als || !als.length) {
                debugger;
                continue;
            }
            if (als.length === 1) {
                if (als[0].accountableAccount?.name?.startsWith("512")) {
                    return false;
                }
            }
            const reconciliation = als.find((al) => al.reconciliation);
            if (reconciliation) {
                return false;
            }
            count++;
        }
        return count;
    }

    get showDeallocation() {
        // if (!this.enableDeallocationSelection && !this.enableDeallocationOnWhere) {
        if (!this.enableDeallocationSelection) {
            return false;
        }
        return true;
    }

    openDeallocateDialog() {
        if (this.enableDeallocationSelection) {
            this.deallocationChoice = "SELECTION";
        } else {
            this.deallocationChoice = "ALL";
        }
        this.showDeallocateDialog = true;
    }

    async deallocateBankTransactions() {
        this.deallocateReconciliationLoading = true;

        let ids;
        let where;
        if (this.deallocationChoice === "SELECTION") {
            ids = this.selected.map((x) => x.id);
        } else if (this.deallocationChoice !== "SELECTION") {
            const variables = getCommonBTQueryVariables(
                this.accountIds,
                this.selectedAccountingClosing,
                this.filter,
                this.selectedCompany.id,
                this.onlyAccounting,
            );
            if (variables?.where) {
                where = variables.where;
            } else {
                debugger;
                return;
            }
        }
        if (ids?.length || where) {
            await this.$apollo.mutate({
                mutation: REMOVE_LINKED_ACCOUNTING_LINES,
                variables: {
                    ids,
                    where,
                },
            });
        }
        this.deallocateReconciliationLoading = false;
        this.showDeallocateDialog = false;
        this.refresh();
    }

    //
    // transaction type
    //
    showTransactionTypeDialog: boolean = false;
    transactionTypeLoading: boolean = false;
    transactionTypeItem = null;
    transactionType = "UNKNOWN";

    get disabledTransactionType() {
        for (const bt of this.selected) {
            if (bt.account.type === "BANK" || bt.status === "DELETED") {
                return false;
            }
        }
        return true;
        // return this.selected.length === 0;
    }

    get transactionTypes() {
        const transactionTypes: Array<{ value: string; text: string }> = [
            { value: "UNKNOWN", text: "" },
            { value: "CREDIT_CARD", text: "" },
            { value: "CASH", text: "" },
            { value: "CHEQUE", text: "" },
            { value: "TRANSFER", text: "" },
            { value: "LUNCHEON_VOUCHER", text: "" },
            { value: "HOLIDAY_VOUCHER", text: "" },
            { value: "PAYPAL", text: "" },
            { value: "DISTRIBUTION_PLATFORM", text: "" },
            { value: "CREDIT_NOTES_ISSUED", text: "" },
            { value: "CREDIT_NOTES_RECOVERY", text: "" },
            { value: "BANK_CHARGE", text: "" },
        ];
        for (const transactionType of transactionTypes) {
            transactionType.text = this.$t(
                "zTillReceipt.transaction." + transactionType.value,
            ) as string;
        }
        return transactionTypes;
    }

    get showTransactionType() {
        return true;
    }

    async modifyTransactionType() {
        this.transactionTypeLoading = true;
        const bts = [];
        if (this.transactionTypeItem && this.transactionTypeItem.account.type === "BANK") {
            this.transactionTypeItem.transactionType = this.transactionType;
            bts.push(this.transactionTypeItem.id);
        } else {
            for (const item of this.selected) {
                if (item.account.type === "BANK") {
                    item.transactionType = this.transactionType;
                    bts.push(item.id);
                }
            }
        }
        if (bts.length && this.selectedCompany && this.selectedCompany.id) {
            await this.$apollo.mutate({
                mutation: gql`
                    mutation ($ids: [ID!]!, $transactionType: BankTransactionTransactionType!) {
                        updateManyBankTransactions(
                            where: { id_in: $ids }
                            data: { transactionType: $transactionType }
                        ) {
                            count
                        }
                    }
                `,
                variables: {
                    ids: bts,
                    transactionType: this.transactionType,
                },
            });
            this.refresh();
        }
        this.transactionTypeItem = null;
        this.transactionTypeLoading = false;
        this.showTransactionTypeDialog = false;
    }

    openCheckWindow() {
        this.activeSendChecks = true;
    }

    openTransactionTypeDialog(bankTransaction) {
        this.transactionTypeItem = bankTransaction;
        if (this.transactionTypeItem) {
            this.transactionType = this.getTransactionType(bankTransaction);
        }
        this.showTransactionTypeDialog = true;
        this.transactionTypeLoading = false;
    }

    closeTransactionTypeDialog() {
        this.showTransactionTypeDialog = false;
        this.transactionTypeItem = null;
    }

    getTransactionType(item) {
        if (item.transactionType) {
            if (item.transactionType !== "UNKNOWN" || !item.type || item.type === "unknown")
                return item.transactionType;
        }
        if (item.type) {
            const transactionType = this.dicoNatureTransactionType[item.type];
            if (transactionType) return transactionType;
        }
        return "UNKNOWN";
    }

    getTransactionTypeText(item) {
        if (item.account.type === "CASH_BOX") {
            return "Caisse";
        }
        return this.$t("zTillReceipt.transaction." + this.getTransactionType(item));
    }

    getTransactionTypeIcon(bankTransaction) {
        const type = this.getTransactionType(bankTransaction);
        if (type) {
            const icon = this.dicoTransactionTypeIcon[type];
            if (icon) return icon;
        }
        return null;
    }

    getTransactionTypeBigram(bankTransaction) {
        const type = this.getTransactionType(bankTransaction);
        if (type) {
            const bigram = this.dicoTransactionTypeBigram[type];
            if (bigram) return bigram;
        }
        return "IN";
    }

    onIntersectNext(
        isIntersecting: boolean,
        entries: IntersectionObserverEntry[],
        observer: IntersectionObserver,
    ) {
        if (this.firstRequestDone) {
            if (isIntersecting) {
                this.pagination.page += 1;
                console.log(this.pagination.rowsPerPage);
            }
            // if (entries[0]?.isIntersecting) {
            //     // this.loadingStateNext = true;
            //     this.pagination.page += 1;
            //     console.log(this.pagination.rowsPerPage);
            // }
        }
    }

    // onIntersectPrevious(entries, observer) {
    //     if (this.firstRequestDone) {
    //         if (entries[0].isIntersecting) {
    //             this.pagination.page -= !this.loadingStateNext ? 1 : 3;
    //             this.loadingStateNext = false;
    //             console.log(this.pagination.rowsPerPage);
    //         }
    //     }
    // }

    analyseBankTransactionWordingsLoading = false;

    async analyseBankTransactionWordings() {
        this.analyseBankTransactionWordingsLoading = true;
        const bts = [];

        for (const item of this.selected) {
            bts.push(item.id);
        }

        if (bts.length && this.account && this.account.id) {
            await this.$apollo.mutate({
                mutation: gql`
                    mutation ($ids: [ID!]!, $bankAccountId: ID!) {
                        analyseBankTransactionWordings(
                            data: { bankTransactions: $ids, bankAccountId: $bankAccountId }
                        ) {
                            status
                        }
                    }
                `,
                variables: {
                    ids: bts,
                    bankAccountId: this.account.id,
                },
            });
            this.refresh();
        }
        this.selected = [];
        this.analyseBankTransactionWordingsLoading = false;
    }

    //
    // Social | State
    //

    subSelectedAccountableAccounts = [];

    subAccountableAccountDialogSelect = false;

    subAccountableAccountTitle = "";

    subInAccountableAccountType = "Selected";

    socialAccountableAccount() {
        this.subSelectedAccountableAccounts = socialAccountableAccounts;
        this.subAccountableAccountDialogSelect = true;
        this.subAccountableAccountTitle = "Social";
        this.subInAccountableAccountType = "Selected";
    }

    stateAccountableAccount() {
        debugger;
        this.subSelectedAccountableAccounts = stateAccountableAccounts;
        this.subAccountableAccountDialogSelect = true;
        this.subAccountableAccountTitle = "Etat";
        this.subInAccountableAccountType = "Selected";
    }

    miscelleanousAccountableAccount() {
        // debugger
        this.subSelectedAccountableAccounts = null;
        this.subAccountableAccountDialogSelect = true;
        this.subAccountableAccountTitle = "Autres";
        this.subInAccountableAccountType = "Selected";
    }

    incomingOutgoingPaymentAccountableAccount() {
        this.subSelectedAccountableAccounts = null;
        this.subAccountableAccountDialogSelect = true;
        this.subAccountableAccountTitle = "Affecter";
        this.subInAccountableAccountType = "IncomingOutgoing";
    }

    async setAccountableAccount(data) {
        await updateBankTransactionAccountableAccount(
            this.$apollo,
            this.$store,
            this.selectedCompany.id,
            this.selected,
            data,
        );
        this.selected = [];
        this.refresh();
        this.refreshKey++;
    }

    //
    // validate auto allocation & auto reconciliation
    //
    get disabledValidateAutoAllocationAndReconciliation() {
        if (!this.selected.length) {
            return true;
        }
        for (const bt of this.selected) {
            if (
                !bt.reconciliationStatus?.allocationSuggestion &&
                !bt.reconciliation?.status?.startsWith("PENDING_VALIDATION")
            ) {
                if (
                    !bt.accountingLines?.find((al) =>
                        al.reconciliation?.status?.startsWith("PENDING_VALIDATION"),
                    )
                ) {
                    return true;
                }
            }
        }
        return false;
    }

    async validateAutoAllocationAndReconciliation() {
        for (const bt of this.selected) {
            let found = false;
            if (
                bt.reconciliation?.id &&
                (bt.reconciliation.status === "PENDING_VALIDATION" ||
                    bt.reconciliation.status === "PENDING_VALIDATION_FROM_CLIENT") &&
                bt.accountingLines.length >= 2
            ) {
                await this.$apollo.mutate({
                    mutation: VALIDATE_RECONCILIATION,
                    variables: {
                        id: bt.reconciliation.id,
                    },
                });
                found = true;
                // if (!this.isIncomingOutgoingPayment) {
                //     // compta d'engagement
                //     this.createUserActivity(bt.reconciliation.id, "VALIDATE");
                // }
            } else {
                for (const al of bt.accountingLines) {
                    if (al.reconciliation?.status?.startsWith("PENDING_VALIDATION")) {
                        await this.$apollo.mutate({
                            mutation: VALIDATE_RECONCILIATION,
                            variables: {
                                id: al.reconciliation.id,
                            },
                        });
                        found = true;
                    }
                }
            }
            if (!found && bt.reconciliationStatus?.allocationSuggestion) {
                await updateBankTransactionAccountableAccount(
                    this.$apollo,
                    this.$store,
                    this.selectedCompany.id,
                    [bt],
                    bt.reconciliationStatus.allocationSuggestion,
                );
            }
        }
        this.selected = [];
        this.refresh();
        this.refreshKey++;
    }

    async validateAutoAllocation() {
        for (const bt of this.selected) {
            if (bt.reconciliationStatus.allocationSuggestion) {
                await updateBankTransactionAccountableAccount(
                    this.$apollo,
                    this.$store,
                    this.selectedCompany.id,
                    [bt],
                    bt.reconciliationStatus.allocationSuggestion,
                );
            }
        }
        this.selected = [];
        this.refresh();
        this.refreshKey++;
    }

    initializeUserActivity() {
        this.activity = new UserActivity("VALIDATE", "RECONCILIATION", moment());
    }

    async createUserActivity(reconciliationId, action) {
        await this.activity.create(
            this.$apollo,
            this.currentUser.id,
            this.selectedCompany.id,
            reconciliationId,
            action,
        );
        this.initializeUserActivity();
    }

    containsDocumentToValidate(group) {
        for (const item of group.items) {
            if (item.doc && item.doc.status !== "VALIDATED") {
                return item;
            }
        }
        return null;
    }

    showValidateReconciliation(group) {
        if (!group.reconciliation || group.reconciliation.acountingItemImport) {
            return false;
        }
        if (
            (group.reconciliation.id && group.reconciliation.status === "PENDING_VALIDATION") ||
            group.reconciliation.status === "PENDING_VALIDATION_FROM_CLIENT"
        ) {
            return true;
        }
        return false;
    }

    //
    // create bankTransaction message
    //

    showCreateBankTransactionMessageDialog = false;

    get selectedBankTransactionsForMessage() {
        let btIds = [];
        for (const item of this.selected) {
            btIds.push(item.id);
        }
        return btIds;
    }

    get aaCharacterCount() {
        return this.$store.state?.accountableAccountTable?.aaCharacterCount || 6;
    }

    computeReconciliationStatus(bt) {
        const reconciliationStatus = {
            allStatus: {},
            type: "",
            notReconciliatedAmountSigned: 0.0,
            toReconciliateAmountSigned: 0.0,
            reconciliatedAmountSigned: 0.0,
            notAllocatedAmountSigned: bt.amount,
            allocatedAmountSigned: 0.0,
            suppliers: [],
            customers: [],
            accountableAccounts: [],
            allocationSuggestion: null,
            category: "",
        };
        let linkedDocuments = [];
        if (bt.reconciliation) {
            reconciliationStatus.type = bt.reconciliation.type;
            reconciliationStatus.allStatus[bt.reconciliation.status] = 1;
            if (bt.reconciliation.documents?.length) {
                linkedDocuments = bt.reconciliation.documents;
            }
        }
        const waitingBankAA = { name: "471000", description: "Comptes d'attente" };
        waitingBankAA.name = waitingBankAA.name.padEnd(this.aaCharacterCount, "0");
        for (const al of bt.accountingLines) {
            if (al.accountableAccount?.name?.startsWith("SALES")) {
                continue;
            }
            if (
                al.accountableAccount?.name?.startsWith("512") &&
                isAccountingZero(al.amountSigned - bt.amount)
            ) {
                if (al.document?.id) {
                    const foundDocId = linkedDocuments.find((ld) => ld.id === al.document.id);
                    if (!foundDocId) {
                        linkedDocuments.push({
                            id: al.document.id,
                            status: al.document.status,
                        });
                    }
                }
                continue;
            }
            reconciliationStatus.allocatedAmountSigned += al.amountSigned;
            reconciliationStatus.notAllocatedAmountSigned += al.amountSigned;

            if (al.accountableAccount?.name && this.$store.state.accountableAccountTable) {
                const dbAA = this.$store.state.accountableAccountTable.accountableAccounts.find(
                    (aa) => aa.name === al.accountableAccount.name,
                );
                if (dbAA?.lettering) {
                    reconciliationStatus.toReconciliateAmountSigned += al.amountSigned;
                    if (al.reconciliation) {
                        reconciliationStatus.reconciliatedAmountSigned += al.amountSigned;
                    } else {
                        reconciliationStatus.notReconciliatedAmountSigned += al.amountSigned;
                    }
                }
            }

            if (al.reconciliation) {
                if (reconciliationStatus.allStatus[al.reconciliation.status]) {
                    reconciliationStatus.allStatus[al.reconciliation.status]++;
                } else {
                    reconciliationStatus.allStatus[al.reconciliation.status] = 1;
                }
                //
                // original or not
                //
                if (al.reconciliation?.accountingLines?.length) {
                    for (const reconciliationAl of al.reconciliation.accountingLines) {
                        if (reconciliationAl.document?.id) {
                            const foundDocId = linkedDocuments.find(
                                (ld) => ld.id === reconciliationAl.document.id,
                            );
                            if (!foundDocId) {
                                linkedDocuments.push({
                                    id: reconciliationAl.document.id,
                                    status: reconciliationAl.document.status,
                                });
                            }
                        }
                    }
                }
            }

            if (al.accountableAccount) {
                if (al.accountableAccount.name.startsWith("455000")) {
                    reconciliationStatus.type = "CURRENT_ACCOUNT";
                } else if (al.accountableAccount.name.startsWith("467000")) {
                    reconciliationStatus.type = "DEBIT_CREDIT_ACCOUNT";
                } else if (al.accountableAccount.name.startsWith("108000")) {
                    reconciliationStatus.type = "OPERATOR_ACCOUNT";
                } else if (socialAccountableAccounts.indexOf(al.accountableAccount.name) !== -1) {
                    reconciliationStatus.type = "SOCIAL";
                } else if (stateAccountableAccounts.indexOf(al.accountableAccount.name) !== -1) {
                    reconciliationStatus.type = "STATE";
                } else if (
                    al.accountableAccount.name.startsWith("511") ||
                    al.accountableAccount.name.startsWith("531") ||
                    al.accountableAccount.name.startsWith("580")
                ) {
                    reconciliationStatus.type = "ZTILL_RECEIPT";
                } else if (al.accountableAccount.name.startsWith("6")) {
                    if (reconciliationStatus.type !== "ZTILL_RECEIPT") {
                        reconciliationStatus.type = "PURCHASE_INVOICE";
                    }
                } else if (al.accountableAccount.name.startsWith("7")) {
                    reconciliationStatus.type = "SALES_INVOICE";
                }
                // else {
                //     reconciliationStatus.type = "DEFAULT"
                // }
                //
                //
                if (
                    !reconciliationStatus.accountableAccounts.find(
                        (a) => a.name === al.accountableAccount.name,
                    )
                ) {
                    reconciliationStatus.accountableAccounts.push(al.accountableAccount);
                }

                if (!this.isAccountingUser) {
                    if (!reconciliationStatus.category) {
                        const r = this.dicoAACategory.find((aa) =>
                            al.accountableAccount.name.startsWith(aa.rootAA),
                        );
                        if (r) {
                            reconciliationStatus.category = r.category;
                        }
                    }
                }
            } else {
                reconciliationStatus.accountableAccounts.push({
                    name: "XXXX",
                    description: "A corriger",
                });
            }

            // if (bt.analysis?.suggestions?.length) {
            // }

            if (reconciliationStatus.type) {
                reconciliationStatus.category = undefined;
            }

            if (al.thirdPartyCompany) {
                if (al.accountableAccount?.name.startsWith("40")) {
                    if (
                        !reconciliationStatus.suppliers.find(
                            (tpc) => tpc.id === al.thirdPartyCompany.id,
                        )
                    ) {
                        reconciliationStatus.suppliers.push(al.thirdPartyCompany);
                    }
                } else if (al.accountableAccount?.name.startsWith("41")) {
                    if (
                        !reconciliationStatus.customers.find(
                            (tpc) => tpc.id === al.thirdPartyCompany.id,
                        )
                    ) {
                        reconciliationStatus.customers.push(al.thirdPartyCompany);
                    }
                }
            }
        }
        if (isAccountingZero(reconciliationStatus.notAllocatedAmountSigned)) {
            reconciliationStatus.notAllocatedAmountSigned = 0;
        }
        if (isAccountingZero(reconciliationStatus.allocatedAmountSigned)) {
            reconciliationStatus.allocatedAmountSigned = 0;
        }
        if (isAccountingZero(reconciliationStatus.notReconciliatedAmountSigned)) {
            reconciliationStatus.notReconciliatedAmountSigned = 0;
        }
        if (isAccountingZero(reconciliationStatus.reconciliatedAmountSigned)) {
            reconciliationStatus.reconciliatedAmountSigned = 0;
        }
        bt.linkedDocuments = linkedDocuments;
        if (!reconciliationStatus.type && bt.accountingLines?.length) {
            reconciliationStatus.type = "DEFAULT";
        }

        if (!reconciliationStatus.accountableAccounts.length && bt.analysis?.suggestions?.length) {
            reconciliationStatus.allocationSuggestion = bt.analysis.suggestions[0];
        } else if (!isAccountingZero(bt.amount + reconciliationStatus.allocatedAmountSigned)) {
            reconciliationStatus.accountableAccounts.push(waitingBankAA);
            bt.accountingLines.push({
                id: "",
                accountableAccount: waitingBankAA,
                amountSigned: -bt.amount - reconciliationStatus.allocatedAmountSigned,
            });
        }
        bt.reconciliationStatus = reconciliationStatus;
    }

    getLinkedDocumentId(bt) {
        if (bt && this.isIncomingOutgoingPayment) {
            return getLinkedDocumentId(bt);
        }
        return null;
    }

    bncDocumentDialog: boolean = false;

    documentDialog: boolean = false;

    async onDocumentValidated() {
        this.bncDocumentDialog = false;
        this.documentDialog = false;
        await this.validateReconciliation();
        this.refresh();
    }

    async validateReconciliation() {
        if (this.containsDocumentToValidate(this.selectedBankTransaction)) {
            debugger;
            return;
        }
        // this.actionLoading = true
        // debugger
        if (
            this.selectedBankTransaction &&
            this.selectedBankTransaction.reconciliation &&
            this.selectedBankTransaction.reconciliation.id &&
            this.selectedBankTransaction.items.length >= 2
        ) {
            await this.$apollo.mutate({
                mutation: VALIDATE_RECONCILIATION,
                variables: {
                    id: this.selectedBankTransaction.reconciliation.id,
                },
            });
            if (!this.isIncomingOutgoingPayment) {
                // compta d'engagement
                this.createUserActivity(this.selectedBankTransaction.reconciliation.id, "VALIDATE");
            }
            // this.actionLoading = false
        }
    }

    onBankTransactionChanged() {
        this.refresh();
    }

    //
    //
    //
    accountableAccountWithSuggestion: boolean = false;

    openAccountableAccountWithSuggestion(bt) {
        this.accountableAccountWithSuggestion = true;
        this.selectedBankTransaction = bt;
    }

    async setAccountableAccountWithSuggestion(data) {
        await updateBankTransactionAccountableAccount(
            this.$apollo,
            this.$store,
            this.selectedCompany.id,
            [this.selectedBankTransaction],
            data,
        );
        this.subSelectedAccountableAccounts = null;
        this.selectedBankTransaction = null;
        this.accountableAccountWithSuggestion = false;
        this.refresh();
    }

    //
    // tag
    //
    setTags(list) {
        // debugger
        this.bankTransactionTags = list;
    }

    /**
     * Get the most recent accounting period from the store
     * @returns The most recent accounting period or null if none are found
     */
    getMostRecentAccountingPeriodFromStore() {
        const storedAccountingPeriods = this.$store.state.accountingPeriod?.accountingPeriods;
        if (!storedAccountingPeriods) {
            return null;
        }

        return getMostRecentAccountingPeriod(storedAccountingPeriods);
    }
}
